import { useFlags } from "launchdarkly-react-client-sdk";
import { usePermissionsV2Query } from "src/graphql";
import { V2PermissionProps, V2ResourceToPermission } from "./types-v2";

export type ResourcePermissionInput<V2Resource extends V2ResourceToPermission> =
  {
    v2: V2PermissionProps<V2Resource>;
  };

/*
 * This hook is used to check if the user has permission to perform an action.
 */
export function useResourcePermission<
  V2Resource extends V2ResourceToPermission,
>(
  permission: ResourcePermissionInput<V2Resource> | undefined,
  options?: {
    enabled?: boolean;
    suspense?: boolean;
    onSuccess?: (isPermitted: boolean) => void;
  },
): { isPermitted: boolean; isLoading: boolean } {
  const v2 =
    permission && "v2" in permission
      ? {
          ...permission.v2,
          // GraphQL does not support union types like string or number, so we explicitly cast it to string
          id:
            "id" in permission.v2 && permission.v2.id
              ? permission.v2.id.toString()
              : undefined,
        }
      : null;

  const flags = useFlags();
  const usePermissionsLoader = Boolean(flags.appUsePermissionsLoader);

  const { data: isPermitted, isLoading } = usePermissionsV2Query(
    { v2 },
    {
      enabled: !!permission && (options?.enabled ?? true),
      suspense: options?.suspense,
      select: (data) => data.isResourceAuthorized.isAuthorized ?? false,
      meta: { isFastEndpoint: true, usePermissionsLoader },
      onSuccess: options?.onSuccess,
    },
  );

  return { isPermitted: isPermitted ?? false, isLoading };
}
